import { useQueryClient } from '@tanstack/react-query'
import {
  Button,
  Col,
  DatePicker,
  Divider,
  Flex,
  Form,
  Input,
  message,
  Modal,
  ModalProps,
  Row
} from 'antd'
import { AnyObject } from 'antd/es/_util/type'
import dayjs, { Dayjs } from 'dayjs'
import { useEffect } from 'react'

import {
  useTemplatesServiceCreateSystemTemplateEndpoint,
  useTemplatesServiceGetSystemTemplateEndpoint,
  useTemplatesServiceGetSystemTemplateEndpointKey,
  useTemplatesServiceGetSystemTemplateListEndpointKey,
  useTemplatesServiceUpdateSystemTemplateEndpoint
} from 'src/api/queries'
import {
  ApiError,
  ClassifierCategory,
  ClassifierType,
  ContentType,
  Permissions
} from 'src/api/requests'
import { requiredRules } from 'src/app-constants'
import {
  ClassifierInfiniteSelect,
  SaveConfirm,
  Text,
  TreatmentContingentTypeInfiniteSelect,
  TreatmentTypeRegimenInfiniteSelect
} from 'src/components'
import {
  normalizeCron,
  TreatmentsConsumablesTables
} from 'src/features/persons/components'
import { useClassifierId, useUser } from 'src/hooks'
import { dayMonthYearBackSlash } from 'src/libs'
import { useLocales } from 'src/locales'
import { formErrorHandler, toDayjs } from 'src/utils'

type Props = {
  templateId?: string
  onCloseModal: () => void
} & ModalProps

export const TreatmentTemplateModal = ({
  templateId,
  onCloseModal,
  ...props
}: Props) => {
  const { t } = useLocales()
  const { hasPermission } = useUser()
  const [form] = Form.useForm()
  const queryClient = useQueryClient()

  const treatmentTypes = useClassifierId(
    ClassifierType.TREATMENT_TYPE,
    ClassifierCategory.TREATMENT
  )

  const selectedTypeId = Form.useWatch('treatment_type', form)

  const { data: templateData, isLoading } =
    useTemplatesServiceGetSystemTemplateEndpoint(
      {
        id: String(templateId)
      },
      [String(templateId)],
      {
        enabled: !!templateId
      }
    )

  const { mutate: createTemplate } =
    useTemplatesServiceCreateSystemTemplateEndpoint({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [useTemplatesServiceGetSystemTemplateListEndpointKey]
        })
        message.success(t('counterparts.template_created'))
        onCancel()
      },
      onError: (error: ApiError) => formErrorHandler(error.body.errors)
    })

  const { mutate: editTemplate } =
    useTemplatesServiceUpdateSystemTemplateEndpoint({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [useTemplatesServiceGetSystemTemplateListEndpointKey]
        })
        queryClient.invalidateQueries({
          queryKey: [useTemplatesServiceGetSystemTemplateEndpointKey]
        })
        message.success(t('common.save_success'))
        onCancel()
      },
      onError: (error: ApiError) => formErrorHandler(error.body.errors)
    })

  const onCancel = () => {
    form.resetFields()
    onCloseModal()
  }

  const onSubmit = async () => {
    const values = await form.validateFields()

    const { name, description, ...content } = values

    const payload = {
      name,
      description,
      isPublic: true,
      type: ContentType.CONSULTATION,
      content: {
        ...content,
        medicine: content?.medicine?.map((item: AnyObject) => ({
          ...item,
          cron: normalizeCron(item?.cron)
        }))
      }
    }

    return templateId
      ? editTemplate({ id: templateId, requestBody: payload })
      : createTemplate({
          requestBody: payload
        })
  }

  const treatmentStartDate = Form.useWatch('treatment_start_date', form)

  const disabledEndDate = (current: Dayjs) => {
    return !dayjs(current).isAfter(treatmentStartDate, 'day')
  }

  useEffect(() => {
    if (templateData) {
      form.setFieldsValue({
        ...templateData?.content,
        name: templateData?.name,
        description: templateData?.description,
        treatment_start_date: toDayjs(
          templateData?.content?.treatment_start_date
        ),
        treatment_end_date: toDayjs(templateData?.content?.treatment_end_date),
        medicine: templateData?.content?.medicine?.map((item: AnyObject) => ({
          ...item,
          cron: normalizeCron(item?.cron),
          endDate: toDayjs(item?.endDate),
          startDate: toDayjs(item?.startDate)
        }))
      })
    }
  }, [templateData])

  return (
    <Modal
      width={1200}
      centered
      footer={false}
      maskClosable={false}
      onCancel={onCancel}
      loading={isLoading}
      title={
        templateId
          ? `${t('common.edit')} ${templateData?.name}`
          : t('templates.create_treatment_template')
      }
      styles={{
        body: {
          maxHeight: '70vh',
          overflowY: 'auto',
          overflowX: 'hidden'
        }
      }}
      {...props}
    >
      <Form form={form} layout="vertical" requiredMark={false}>
        <Flex vertical gap={16}>
          <Row gutter={[16, 16]}>
            <Col span={24}>
              <Form.Item
                name="name"
                label={t(`medicine_register.units.name`)}
                rules={requiredRules}
              >
                <Input
                  className="full-width"
                  placeholder={t(`medicine_register.units.name`)}
                />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item
                name="description"
                label={t(`common.description`)}
                rules={requiredRules}
              >
                <Input.TextArea
                  className="full-width"
                  placeholder={t(`common.description`)}
                />
              </Form.Item>
            </Col>
          </Row>

          <Divider />

          <Flex gap={16} align="center" wrap>
            <Form.Item
              name="treatment_type"
              label={t('treatments.treatment_type')}
              rules={requiredRules}
            >
              <ClassifierInfiniteSelect
                id={treatmentTypes}
                placeholder={t('treatments.treatment_type')}
              />
            </Form.Item>

            <Form.Item
              name="preventionTreatmentContingentTypeId"
              label={t('investigations.treatment_reason')}
            >
              <TreatmentContingentTypeInfiniteSelect
                typesId={selectedTypeId}
                disabled={!selectedTypeId}
              />
            </Form.Item>

            <Form.Item
              name="treatmentTypeRegimenId"
              label={t('treatments.regiment_type')}
              rules={requiredRules}
            >
              <TreatmentTypeRegimenInfiniteSelect
                typesId={[selectedTypeId]}
                disabled={!selectedTypeId}
              />
            </Form.Item>

            <Form.Item
              name="treatment_start_date"
              label={t('persons.treatment.start_date')}
              rules={requiredRules}
            >
              <DatePicker
                className="full-width"
                placeholder={t('persons.treatment.start_date')}
                format={dayMonthYearBackSlash}
              />
            </Form.Item>

            <Form.Item
              name="treatment_end_date"
              label={t('persons.treatment.end_date')}
              rules={requiredRules}
            >
              <DatePicker
                className="full-width"
                placeholder={t('persons.treatment.end_date')}
                format={dayMonthYearBackSlash}
                disabled={!treatmentStartDate}
                disabledDate={disabledEndDate}
              />
            </Form.Item>
          </Flex>
          {hasPermission(
            Permissions.TREATMENT_TREATMENT_PLAN_VIEW_PRESCRIBED
          ) && (
            <>
              <Divider />
              <Flex key={1} vertical gap={24}>
                <Text size="xl">
                  {t('persons.treatment.treatment_prescribed')}
                </Text>

                <Form.Item name="medicine" rules={requiredRules}>
                  <Form.List name="medicine">
                    {(fields, { remove }) => (
                      <TreatmentsConsumablesTables
                        fields={fields}
                        remove={remove}
                        form={form}
                      />
                    )}
                  </Form.List>
                </Form.Item>
              </Flex>
            </>
          )}

          <Flex justify="center" gap={8}>
            <Button onClick={onCancel}>{t('common.cancel')}</Button>

            <SaveConfirm
              title={
                templateId
                  ? `${t('notification_templates.edit_template')}?`
                  : `${t('notification_templates.create')}?`
              }
              placement="top"
              onConfirm={onSubmit}
            >
              <Button type="primary">
                {templateId
                  ? t('notification_templates.edit_template')
                  : t('notification_templates.create')}
              </Button>
            </SaveConfirm>
          </Flex>
        </Flex>
      </Form>
    </Modal>
  )
}
